// @ts-nocheck
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { Handler, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { CatalogPage } from "@insite/client-framework/Services/CategoryService";
import {
    GetProductCollectionApiV2Parameter,
    ProductExpandTokens,
} from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    HandleSortOrderDefault,
    LoadRealTimePrices,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { ProductFilters } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { ProductModel, RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import { dispatchProductImpressions } from "@store/Services/AnalyticsService";

const productListSortTypeCookie = "productListSortType";

const enum pageType {
    productListPage = "Product List Page",
    searchPage = "Search Page",
}

interface Parameter {
    queryString: string;
    path: string;
    isSearch: boolean;
}

export interface DisplayProductsResult {
    products?: ProductModel[];
    productInfosByProductId: SafeDictionary<ProductInfo>;
    productFilters: ProductFilters;
    isFiltered: boolean;
    unfilteredApiParameter?: GetProductCollectionApiV2Parameter;
    filteredApiParameter?: GetProductCollectionApiV2Parameter;
    catalogPage?: CatalogPage;
}

interface Props {
    apiParameter: GetProductCollectionApiV2Parameter;
    idByPath?: SafeDictionary<string>;
    result: DisplayProductsResult;
    pricingLoaded?: true;
    expandTokens?: ProductExpandTokens;
}

type HandlerType = Handler<Parameter, Props>;

export const StorePopulateApiParameter: HandlerType = props => {
    const filters = props.result.productFilters;
    const stockedRestrictedProductsToggle = localStorage.getItem("isRestrictedProductsToggleOn"); // Store Custom Code
    const {
        query,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
        pageCategoryId,
        pageBrandId,
        pageProductLineId,
        ...apiParameter
    } = filters;
    props.apiParameter = {
        ...apiParameter,
        search: query,
        categoryId: filters.categoryId || filters.pageCategoryId,
        searchWithin: searchWithinQueries?.join(" "),
        includeSuggestions: filters.includeSuggestions !== false,
        brandIds: filters.pageBrandId ? [filters.pageBrandId] : brandIds,
        productLineIds: filters.pageProductLineId ? [filters.pageProductLineId] : productLineIds,
        priceFilters,
        attributeValueIds,
        expand: props.expandTokens || [],
        applyPersonalization: true,
        includeAttributes: ["includeOnProduct"],
        /* Store Custom Code - Start */
        additionalQueryStringParameters: {
            restrictionTypes: stockedRestrictedProductsToggle || "false",
        },
        /* Store Custom Code - End */
    };
};

export const StoreLoadRealTimePrices: HandlerType = async props => {
    const {
        result: { productInfosByProductId },
    } = props;
    const productIds = Object.keys(productInfosByProductId); // Store Custom Code
    if (productIds.length === 0) {
        return;
    }

    /* Store Custom Code - Start */
    const state = props.getState();
    const { isSearch } = props.parameter;
    const currency = state.context.session.currency?.currencyCode;
    const page = isSearch ? pageType.searchPage : pageType.productListPage;
    const session = getSession(state);
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

    const handleProductsImpressions = (realTimePricing?: RealTimePricingModel) => {
        const products = props.result.products?.map(product => {
            return {
                ...product,
                unitListPrice: realTimePricing?.realTimePricingResults?.find(p => p.productId === product.id)
                    ?.unitListPrice,
            };
        });
        dispatchProductImpressions(products as ProductModel[], currency, page);
    };

    if (!isAuthenticated) {
        handleProductsImpressions();
    }
    /* Store Custom Code - End */

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            /* Store Custom Code - Start */
            if (isAuthenticated) {
                handleProductsImpressions(realTimePricing);
            }
            /* Store Custom Code - End */
            props.dispatch({
                type: "Pages/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });
            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "Pages/ProductList/FailedLoadRealTimePricing",
            });
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    const settings = getSettingsCollection(props.getState());

    if (IS_SERVER_SIDE && getIsWebCrawler() && !settings.websiteSettings.includeRealTimeInventoryAndPricingForSeo) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }

    const productSettings = settings.productSettings;
    if (productSettings.canSeePrices && productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const StoreHandleSortOrderDefault: HandlerType = props => {
    if (!props.apiParameter.sort) {
        if (!props.parameter.isSearch && props.apiParameter.search?.length !== 0) {
            props.apiParameter.sort = getCookie(productListSortTypeCookie);
        }
    } else {
        setCookie(productListSortTypeCookie, props.apiParameter.sort);
    }
};

replaceInChain(chain, PopulateApiParameter, StorePopulateApiParameter);
replaceInChain(chain, LoadRealTimePrices, StoreLoadRealTimePrices);
replaceInChain(chain, HandleSortOrderDefault, StoreHandleSortOrderDefault);
